import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { TwSolicitudCamValidationViewModel } from '../../../types/solicitudcam-datosbasicos.types';
import {
    EnumIdsTwTcEstadoAceptacionSolicitudCam,
    EnumIdsTcEstadoSolicitudCam,
    EnumIdsTwTcEstadoValidacionSolicitudCam,
    EnumDescripcionTcTipoSolicitudCamPlural,
    EnumDescripcionTcTipoSolicitudCamSingular,
    EnumIdsTcTipoSolicitudCam,
} from '../../../types/enum';
import { SelectItem } from 'primeng/api';

@Component({
    selector: 'app-validacion-aceptacion-form',
    templateUrl: './validacion-aceptacion-form.component.html',
    styleUrls: ['./validacion-aceptacion-form.component.scss'],
})
export class ValidacionAceptacionFormComponent extends ComponentBase implements OnChanges, OnInit {
    @Input() readonly: boolean;
    @Input() solicitudCam: TwSolicitudCamValidationViewModel;
    @Input() formGroup: FormGroup;

    isCollapsedCabeceraVal = false;
    isCollapsedCabeceraAcept = false;

    // Inicializo así la lista, en vez de invocar al servicio CampaniasGlobalService.getEstadosValidacionSolicitudCam
    // porque el servicio no devuelve el icono asociado a la opción.
    // El orden de éstos items importa de cara a los estilos definidos en el scss de éste mismo componente.
    listOpcionesValidacion: SelectItem[] = [
        { label: 'Pendiente', value: EnumIdsTwTcEstadoValidacionSolicitudCam.PENDIENTE, icon: 'fa fa-clock-o' },
        { label: 'Validada', value: EnumIdsTwTcEstadoValidacionSolicitudCam.VALIDADA, icon: 'fa fa-thumbs-o-up' },
        { label: 'Devolver a Operador', value: EnumIdsTwTcEstadoValidacionSolicitudCam.DEVUELTA, icon: 'fa fa-hand-o-left' },
    ];
    // Inicializo así la lista, en vez de invocar al servicio CampaniasGlobalService.getEstadosAceptacionSolicitudCam
    // porque el servicio no devuelve el icono asociado a la opción
    // El orden de éstos items importa de cara a los estilos definidos en el scss de éste mismo componente.
    listOpcionesAceptacion: SelectItem[] = [
        { label: 'Pendiente', value: EnumIdsTwTcEstadoAceptacionSolicitudCam.PENDIENTE, icon: 'fa fa-clock-o' },
        { label: 'Aceptada', value: EnumIdsTwTcEstadoAceptacionSolicitudCam.ACEPTADA, icon: 'fa fa-thumbs-up' },
        { label: 'Devolver a Validador', value: EnumIdsTwTcEstadoAceptacionSolicitudCam.DEVUELTA_A_EA, icon: 'fa fa-hand-o-left' },
        { label: 'Rechazada', value: EnumIdsTwTcEstadoAceptacionSolicitudCam.RECHAZADA, icon: 'fa fa-thumbs-o-down' },
    ];

    constructor(
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.form = this.formGroup; // componentBase uses this.form
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.solicitudCam?.currentValue) {
            this.updateForm();
        }
    }

    private updateForm(): void {
        this.form = this.formGroup; // componentBase uses this.form
        this.updateFormValidacion();
        this.updateFormAceptacion();

        this.submitted = true;

        this.manageValidatorsForObservacionesValidacion(this.estadoSolicitudValidada);
        if (this.isCA) {
            this.manageValidatorsForObservacionesAceptacion(this.estadoSolicitudRechazada);
            // Los comentarios de la aceptación serán obligatorios si la solicitud no estuviera aceptada.
        }
    }

    private updateFormValidacion() {
        if (!this.validacionFormGroup) {
            return;
        }

        let estadoValidacion: number | undefined = undefined;
        if (this.solicitudCam?.validacionData) {
            let usuarioValidacion;
            if (this.solicitudCam.validacionData.IdUsuarioValidacionTecnicoEa) {
                // Si la validación la realizado un usuario EA
                usuarioValidacion = this.solicitudCam.validacionData.UsuarioValidacionTecnicoEa;
                estadoValidacion = this.solicitudCam.validacionData.IdEstadoValidacionEa;
            } else if (this.solicitudCam.validacionData.IdUsuarioValidacionCa) {
                // Si la validación la realizado un usuario CA
                usuarioValidacion = this.solicitudCam.validacionData.UsuarioValidacionCa;
                estadoValidacion = this.solicitudCam.validacionData.IdEstadoValidacionCa;
            }

            this.validacionFormGroup.patchValue({
                estadoValidacion: estadoValidacion,
                fechaValidacion: this.solicitudCam.validacionData.Fecha ? new Date(this.solicitudCam.validacionData.Fecha) : '',
                usuarioValidacion: usuarioValidacion,
                observacionesValidacion: this.solicitudCam.validacionData.Observaciones,
            });
        } else {
            this.validacionFormGroup.patchValue({
                estadoValidacion: estadoValidacion,
            });
        }
    }

    private updateFormAceptacion() {
        if (!this.aceptacionFormGroup) {
            return;
        }
        if (this.solicitudCam?.aceptacionData) {
            this.aceptacionFormGroup.patchValue({
                estadoAceptacion: this.solicitudCam.aceptacionData.IdEstadoAceptacion || EnumIdsTwTcEstadoValidacionSolicitudCam.PENDIENTE,
                fechaAceptacion: this.solicitudCam.aceptacionData.Fecha ? new Date(this.solicitudCam.aceptacionData.Fecha) : '',
                usuarioAceptacion: this.solicitudCam.aceptacionData.UsuarioCa,
                observacionesAceptacion: this.solicitudCam.aceptacionData.Observaciones,
            });
        } else {
            this.aceptacionFormGroup.patchValue({
                estadoAceptacion: undefined,
            });
        }
    }

    onValidaChange(validaSelection: any): void {
        if (validaSelection?.value === undefined || validaSelection?.value === null) {
            // Sobreescribimos cuando no llega valor. Esto solo ocurre cuando se vuelve a seleccionar la misma opción que ya está seleccionada
            validaSelection.value = EnumIdsTwTcEstadoValidacionSolicitudCam.PENDIENTE;
            this.validacionFormGroup.value.estadoValidacion = EnumIdsTwTcEstadoValidacionSolicitudCam.PENDIENTE; // Cuando se desmarca, volvemos a marcar la opción por defecto.
        }
        const validada = validaSelection.value === EnumIdsTwTcEstadoValidacionSolicitudCam.VALIDADA;

        this.manageValidatorsForObservacionesValidacion(validada);
        // Los comentarios de la validación son obligatorios cuando no está validada,

        if (this.isCA) {
            this.aceptacionFormGroup.patchValue({
                estadoAceptacion: EnumIdsTwTcEstadoAceptacionSolicitudCam.PENDIENTE,
            });
            if (!validada) {
                this.aceptacionFormGroup.controls['estadoAceptacion'].disable();
                this.aceptacionFormGroup.controls['observacionesAceptacion'].disable();
            } else {
                this.aceptacionFormGroup.controls['estadoAceptacion'].enable();
                this.aceptacionFormGroup.controls['observacionesAceptacion'].enable();
            }
            this.manageValidatorsForObservacionesAceptacion(!validada);
            // Los comentarios de la aceptación no son obligatorios si estuviera validada.
        }
    }

    private manageValidatorsForObservacionesValidacion(validada: boolean): void {
        if (validada) {
            this.removeValidators('validacion.observacionesValidacion');
        } else {
            this.addValidator('validacion.observacionesValidacion', Validators.required);
        }
    }

    onAceptaChange(aceptaSelection: any): void {
        if (aceptaSelection?.value === undefined || aceptaSelection?.value === null) {
            // Sobreescribimos cuando no llega valor. Esto solo ocurre cuando se vuelve a seleccionar la misma opción que ya está seleccionada
            aceptaSelection.value = EnumIdsTwTcEstadoAceptacionSolicitudCam.PENDIENTE;
            this.aceptacionFormGroup.value.estadoAceptacion = EnumIdsTwTcEstadoAceptacionSolicitudCam.PENDIENTE; // Cuando se desmarca, volvemos a marcar la opción por defecto.
        }
        const aceptada = aceptaSelection.value === EnumIdsTwTcEstadoAceptacionSolicitudCam.ACEPTADA;
        this.manageValidatorsForObservacionesAceptacion(aceptada);
        // Los comentarios de la aceptación no son obligatorios si no está aceptada.
    }

    private manageValidatorsForObservacionesAceptacion(rechazada: boolean): void {
        if (rechazada) {
            this.removeValidators('aceptacion.observacionesAceptacion');
        } else {
            this.addValidator('aceptacion.observacionesAceptacion', Validators.required);
        }
    }

    get labelForUserValidacion(): string {
        if (this.solicitudCam?.validacionData?.IdUsuarioValidacionCa) {
            return 'Comunidad Autónoma';
        } else if (this.solicitudCam?.validacionData?.IdUsuarioValidacionTecnicoEa) {
            return 'Entidad Auditora';
        }
        return '';
    }

    get observacionesValidacionRequired(): boolean | null {
        const invalid = this.isFieldInvalid('validacion.observacionesValidacion', 'required');
        return this.validacionFormGroup?.value.estadoValidacion !== EnumIdsTwTcEstadoValidacionSolicitudCam.VALIDADA && invalid;
    }

    get observacionesAceptacionRequired(): boolean | null {
        const invalid = this.isFieldInvalid('aceptacion.observacionesAceptacion', 'required');
        return this.isCA && this.aceptacionFormGroup?.value.estadoAceptacion !== EnumIdsTwTcEstadoAceptacionSolicitudCam.ACEPTADA && invalid;
    }

    get pendienteEnvioMessage(): string {
        return 'El proceso de Validación / Aceptación comenzará una vez la Solicitud se haya enviado';
    }

    get estadoSolicitudBorrador(): boolean {
        return this.solicitudCam?.IdEstadoSolicitudCam === EnumIdsTcEstadoSolicitudCam.ID_BORRADOR;
    }

    private get estadoSolicitudValidada(): boolean {
        // @author: scalero1- Se podría usar IdEstadoValidacionEa y IdEstadoValidacionCa, pero complica la lógica
        return this.solicitudCam?.IdEstadoSolicitudCam === EnumIdsTcEstadoSolicitudCam.ID_VALIDADA;
    }

    get estadoSolicitudRechazada(): boolean {
        // @author: scalero1 - Se podría usar IdEstadoAceptacion pero complica la lógica
        return this.solicitudCam?.IdEstadoSolicitudCam === EnumIdsTcEstadoSolicitudCam.ID_RECHAZADA;
    }

    get validacionFormGroup(): FormGroup {
        return this.form?.get('validacion') as FormGroup;
    }

    get aceptacionFormGroup(): FormGroup {
        return this.form?.get('aceptacion') as FormGroup;
    }

    get esRechazada(): boolean {
        return this.aceptacionFormGroup?.value.estadoAceptacion === EnumIdsTwTcEstadoAceptacionSolicitudCam.RECHAZADA;
    }

    get titleByTipoSolicitud(): any {
        if (!this.solicitudCam)
            return { plural: EnumDescripcionTcTipoSolicitudCamPlural.RECINTOS, singular: EnumDescripcionTcTipoSolicitudCamSingular.RECINTO };

        switch (this.solicitudCam?.IdTipoSolicitudCam) {
            case EnumIdsTcTipoSolicitudCam.ID_CONFECCION:
                return { plural: EnumDescripcionTcTipoSolicitudCamPlural.CONFECCION, singular: EnumDescripcionTcTipoSolicitudCamSingular.CONFECCION };
            case EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.EMPRESA_DESHIDRATACION,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.EMPRESA_DESHIDRATACION,
                };
            case EnumIdsTcTipoSolicitudCam.ID_PARCELA:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.PARCELA,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.PARCELA,
                };
            case EnumIdsTcTipoSolicitudCam.ID_INVERNADERO:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.INVERNADERO,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.INVERNADERO,
                };
            default:
                return { plural: EnumDescripcionTcTipoSolicitudCamPlural.RECINTOS, singular: EnumDescripcionTcTipoSolicitudCamSingular.RECINTO };
        }
    }
}
